import { Component, lazy, Suspense, type ReactNode } from "react";
import {
  HashRouter,
  MemoryRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import Loader from "@/components/Loader";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ExamProvider } from "@/context/ExamContext";
import { PracticeSessionProvider } from "@/context/PracticeSessionContext";
import { SettingsProvider } from "@/context/SettingsContext";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OpenInExcelPage from "@/pages/OpenInExcelPage";
import { EXTRA_TOOLS_ENABLED } from "@/lib/features";

const MainLayout = lazy(() => import("@/pages/MainLayout"));
const ProjectListPage = lazy(() => import("@/features/projects/ProjectListPage"));
const FuncionesListPage = lazy(
  () => import("@/features/projects/FuncionesListPage")
);
const PracticaDetailPage = lazy(
  () => import("@/features/projects/PracticaDetailPage")
);
const ProjectWorkspacePage = lazy(
  () => import("@/features/projects/ProjectWorkspacePage")
);
const TranslatorPage = lazy(() => import("@/features/translator/TranslatorPage"));
const ChatbotPage = lazy(() => import("@/features/chatbot/ChatbotPage"));
const EnigmaPage = lazy(() => import("@/features/enigma/EnigmaPage"));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: "" };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, fontFamily: "Poppins, sans-serif" }}>
          <h2 style={{ color: "red" }}>Error al cargar la aplicación</h2>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
            {this.state.error}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

function RouterWrapper({ children }: { children: ReactNode }) {
  const historyWorks =
    typeof window.history?.replaceState === "function" &&
    typeof window.history?.pushState === "function";

  if (historyWorks) {
    return <HashRouter>{children}</HashRouter>;
  }

  return <MemoryRouter>{children}</MemoryRouter>;
}

function AppRoutes({ isExcelHost }: { isExcelHost: boolean }) {
  const { session, loading, isPasswordRecovery } = useAuth();

  if (loading) return <Loader />;

  if (isPasswordRecovery) {
    return (
      <Routes>
        <Route path="*" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  // Sign-in and the practices dashboard only make sense inside Excel's
  // task pane. The reset-password link always opens in a plain browser,
  // so it stays reachable regardless of host.
  if (!isExcelHost) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<OpenInExcelPage />} />
      </Routes>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <ExamProvider>
      <PracticeSessionProvider>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route element={<MainLayout />}>
              <Route path="/projects" element={<ProjectListPage />} />
              <Route path="/funciones" element={<FuncionesListPage />} />
              <Route
                path="/practicas/:practicaId"
                element={<PracticaDetailPage />}
              />
              <Route
                path="/practicas/:practicaId/projects/:projectId"
                element={<ProjectWorkspacePage />}
              />
              <Route path="/translator" element={<TranslatorPage />} />
              {EXTRA_TOOLS_ENABLED && (
                <>
                  <Route path="/chatbot" element={<ChatbotPage />} />
                  <Route path="/enigma" element={<EnigmaPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/projects" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </PracticeSessionProvider>
    </ExamProvider>
  );
}

export default function App({ isExcelHost }: { isExcelHost: boolean }) {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SettingsProvider>
          <RouterWrapper>
            <AppRoutes isExcelHost={isExcelHost} />
          </RouterWrapper>
        </SettingsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
